import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Routes } from '#Routes';
import { Row, Col, Card, Button, Badge, Modal, Form, Alert, Spinner, Table } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import {
  useFetchCalculatorDashboardQuery,
  useFetchCalculatorDescriptionsQuery,
  useFetchAvailableCalculatorsQuery,
  useRunCalculatorMutation,
  useUpdateMedicalDataMutation,
  useFetchCalculatorHistoryQuery,
  useFetchMentalHealthHistoryQuery,
  useFetchQuestionnaireQuestionsQuery,
  useSubmitQuestionnaireMutation,
  usePatchCalculatorResultMutation,
  useDeleteCalculatorResultMutation,
  useFetchBloodPressureReadingsQuery,
  FETCH_BLOOD_PRESSURE_READINGS_ARG,
  normalizeBloodPressureReadingsList,
} from '#store';
import moment from 'moment';
import { sortBloodPressureReadingsOldestToNewest } from '#utils/bloodPressureReadings';
import { BloodPressureReadingsChart } from '#components/dashboard/BloodPressureReadingsChart';
import { AddBloodPressureModal } from '#components/dashboard/AddBloodPressureModal';
import ReactApexChart from 'react-apexcharts';
import { InformationCircleIcon } from '@heroicons/react/solid';

// ─── constants ──────────────────────────────────────────────────────────────

const CALCULATOR_LABELS = {
  TYG: 'TyG',
  EGFR: 'eGFR',
  FIB4: 'FIB-4',
  SCORE2: 'SCORE2',
  SCORE2_OP: 'SCORE2-OP',
  TOTAL_HDL_RATIO: 'Cholesterol TC/HDL',
  NON_HDL: 'Non-HDL',
  TG_HDL_RATIO: 'TG/HDL ratio',
  AG_RATIO: 'A/G Ratio',
  NLR: 'NLR',
  WHTR: 'WHtR',
  PHQ9: 'PHQ-9',
  GAD7: 'GAD-7',
};

const CALCULATOR_DESCRIPTIONS = {
  TYG: {
    pl: 'Indeks triglicerydy–glukoza (TyG)',
    en: 'Triglyceride–glucose index (TyG)',
  },
  EGFR: {
    pl: 'Szacowany wskaźnik filtracji kłębuszkowej (nerki)',
    en: 'Estimated glomerular filtration rate (kidney)',
  },
  FIB4: { pl: 'Wskaźnik włóknienia wątroby', en: 'Liver fibrosis index' },
  SCORE2: { pl: 'Ryzyko sercowo-naczyniowe (< 70 lat)', en: 'Cardiovascular risk (< 70 years)' },
  SCORE2_OP: { pl: 'Ryzyko sercowo-naczyniowe (≥ 70 lat)', en: 'Cardiovascular risk (≥ 70 years)' },
  TOTAL_HDL_RATIO: { pl: 'Stosunek cholesterolu TC/HDL', en: 'Total/HDL cholesterol ratio' },
  NON_HDL: { pl: 'Cholesterol non-HDL', en: 'Non-HDL cholesterol' },
  TG_HDL_RATIO: { pl: 'Stosunek triglicerydów do HDL', en: 'Triglycerides to HDL ratio' },
  AG_RATIO: { pl: 'Stosunek albumina/globulina', en: 'Albumin/globulin ratio' },
  NLR: { pl: 'Stosunek neutrofile/limfocyty', en: 'Neutrophil-to-lymphocyte ratio' },
  WHTR: { pl: 'Stosunek obwodu talii do wzrostu', en: 'Waist-to-height ratio' },
  PHQ9: { pl: 'Skala depresji PHQ-9', en: 'PHQ-9 Depression Scale' },
  GAD7: { pl: 'Skala lęku GAD-7', en: 'GAD-7 Anxiety Scale' },
};

/** Short card/modal title where CALCULATOR_LABELS was Polish-only (e.g. TG/HDL). */
function getCalculatorCardTitle(calcType, lang) {
  const pl = typeof lang === 'string' && lang.startsWith('pl');
  if (calcType === 'TG_HDL_RATIO') {
    return pl ? 'Stosunek TG/HDL' : 'TG/HDL ratio';
  }
  return CALCULATOR_LABELS[calcType] || calcType;
}

/** WHtR: strip Polish typographic quotes „…” from interpretation text (e.g. after “in range”). */
function formatWhtrInterpretationForDisplay(text) {
  if (text == null || typeof text !== 'string') return text;
  return text.replace(/„|”/g, '');
}

const CATEGORY_COLORS = {
  normal: 'success',
  desirable: 'success',
  optimal: 'success',
  healthy: 'success',
  minimal: 'success',
  low: 'success',
  LOW: 'success', // SCORE2 / SCORE2-OP: niskie ryzyko — jak pozostałe korzystne (zielony)
  low_risk: 'success',
  borderline: 'warning',
  moderate: 'warning',
  MODERATE: 'warning', // SCORE2 / SCORE2-OP: moderate risk
  mild: 'warning',
  elevated: 'warning',
  high: 'danger',
  HIGH: 'danger', // SCORE2 / SCORE2-OP: elevated risk
  high_risk: 'danger',
  high_fibrosis_risk: 'danger',
  low_fibrosis_risk: 'success',
  moderate_fibrosis_risk: 'warning',
  indeterminate: 'warning',
  intermediate: 'warning',
  negligible: 'success',
  moderately_severe: 'danger',
  severe: 'danger',
  very_high: 'danger',
  moderate_to_severe: 'danger',
  // eGFR / KDIGO (typowe wartości z API)
  no_ckd: 'success',
  ckd_g1: 'success',
  ckd_g2: 'success',
  g1: 'success',
  g2: 'success',
  stage_1: 'success',
  stage_2: 'success',
  ckd_g3a: 'warning',
  ckd_g3b: 'warning',
  g3a: 'warning',
  g3b: 'warning',
  stage_3a: 'warning',
  stage_3b: 'warning',
  ckd_g4: 'danger',
  ckd_g5: 'danger',
  g4: 'danger',
  g5: 'danger',
  stage_4: 'danger',
  stage_5: 'danger',
  kidney_failure: 'danger',
  esrd: 'danger',
  decreased: 'warning',
  mildly_decreased: 'warning',
  moderately_decreased: 'warning',
  severely_decreased: 'danger',
  mild_decrease: 'warning',
  moderate_decrease: 'warning',
  severe_decrease: 'danger',
};

// Risk category labels (backend often returns snake_case, e.g. FIB-4)
const RISK_CATEGORY_LABELS = {
  pl: {
    normal: 'W normie',
    healthy: 'W normie',
    desirable: 'Pożądany',
    optimal: 'Optymalny',
    minimal: 'Minimalny',
    low: 'Niskie',
    LOW: 'Niskie ryzyko',
    low_risk: 'Niskie ryzyko',
    borderline: 'Graniczne',
    moderate: 'Umiarkowane',
    MODERATE: 'Umiarkowane ryzyko',
    mild: 'Łagodne',
    elevated: 'Podwyższone',
    high_risk: 'Wysokie ryzyko',
    high: 'Wysokie',
    HIGH: 'Podwyższone ryzyko',
    high_fibrosis_risk: 'Wysokie ryzyko włóknienia',
    low_fibrosis_risk: 'Niskie ryzyko włóknienia',
    moderate_fibrosis_risk: 'Umiarkowane ryzyko włóknienia',
    moderately_severe: 'Umiarkowanie ciężkie',
    severe: 'Ciężkie',
    very_high: 'Bardzo wysokie',
    moderate_to_severe: 'Umiarkowane do ciężkiego',
    indeterminate: 'Nieokreślone',
    intermediate: 'Pośrednie',
    negligible: 'Znikome',
    no_ckd: 'Brak przewlekłej choroby nerek',
    ckd_g1: 'G1 — prawidłowa lub podwyższona filtracja',
    ckd_g2: 'G2 — lekko obniżona filtracja',
    ckd_g3a: 'G3a — umiarkowanie obniżona filtracja',
    ckd_g3b: 'G3b — znacznie obniżona filtracja',
    ckd_g4: 'G4 — ciężko obniżona filtracja',
    ckd_g5: 'G5 — niewydolność nerek',
    g1: 'G1 — prawidłowa lub podwyższona filtracja',
    g2: 'G2 — lekko obniżona filtracja',
    g3a: 'G3a — umiarkowanie obniżona filtracja',
    g3b: 'G3b — znacznie obniżona filtracja',
    g4: 'G4 — ciężko obniżona filtracja',
    g5: 'G5 — niewydolność nerek',
    stage_1: 'G1 — prawidłowa lub podwyższona filtracja',
    stage_2: 'G2 — lekko obniżona filtracja',
    stage_3a: 'G3a — umiarkowanie obniżona filtracja',
    stage_3b: 'G3b — znacznie obniżona filtracja',
    stage_4: 'G4 — ciężko obniżona filtracja',
    stage_5: 'G5 — niewydolność nerek',
    kidney_failure: 'Niewydolność nerek',
    esrd: 'Paliatywna terapia nerek',
    decreased: 'Obniżona filtracja',
    mildly_decreased: 'Lekko obniżona filtracja',
    moderately_decreased: 'Umiarkowanie obniżona filtracja',
    severely_decreased: 'Ciężko obniżona filtracja',
    mild_decrease: 'Lekko obniżona filtracja',
    moderate_decrease: 'Umiarkowanie obniżona filtracja',
    severe_decrease: 'Ciężko obniżona filtracja',
  },
  en: {
    normal: 'Normal',
    healthy: 'Healthy',
    desirable: 'Desirable',
    optimal: 'Optimal',
    minimal: 'Minimal',
    low: 'Low',
    LOW: 'Low risk',
    low_risk: 'Low risk',
    borderline: 'Borderline',
    moderate: 'Moderate',
    MODERATE: 'Moderate risk',
    mild: 'Mild',
    elevated: 'Elevated',
    high_risk: 'High risk',
    high: 'High',
    HIGH: 'High risk',
    high_fibrosis_risk: 'High fibrosis risk',
    low_fibrosis_risk: 'Low fibrosis risk',
    moderate_fibrosis_risk: 'Moderate fibrosis risk',
    moderately_severe: 'Moderately severe',
    severe: 'Severe',
    very_high: 'Very high',
    moderate_to_severe: 'Moderate to severe',
    indeterminate: 'Indeterminate',
    intermediate: 'Intermediate',
    negligible: 'Negligible',
    no_ckd: 'No chronic kidney disease',
    ckd_g1: 'G1 — normal or high filtration',
    ckd_g2: 'G2 — mildly decreased filtration',
    ckd_g3a: 'G3a — moderately decreased filtration',
    ckd_g3b: 'G3b — severely decreased filtration',
    ckd_g4: 'G4 — very severely decreased filtration',
    ckd_g5: 'G5 — kidney failure',
    g1: 'G1 — normal or high filtration',
    g2: 'G2 — mildly decreased filtration',
    g3a: 'G3a — moderately decreased filtration',
    g3b: 'G3b — severely decreased filtration',
    g4: 'G4 — very severely decreased filtration',
    g5: 'G5 — kidney failure',
    stage_1: 'G1 — normal or high filtration',
    stage_2: 'G2 — mildly decreased filtration',
    stage_3a: 'G3a — moderately decreased filtration',
    stage_3b: 'G3b — severely decreased filtration',
    stage_4: 'G4 — very severely decreased filtration',
    stage_5: 'G5 — kidney failure',
    kidney_failure: 'Kidney failure',
    esrd: 'End-stage renal disease',
    decreased: 'Decreased filtration',
    mildly_decreased: 'Mildly decreased filtration',
    moderately_decreased: 'Moderately decreased filtration',
    severely_decreased: 'Severely decreased filtration',
    mild_decrease: 'Mild decrease',
    moderate_decrease: 'Moderate decrease',
    severe_decrease: 'Severe decrease',
  },
};

/** Field labels: `health-calculators-page.manualFields.<key>`; select options: i18nKey or fixed label (units). */
const MANUAL_INPUTS = {
  TYG: [
    { key: 'fasting_glucose', type: 'number', step: '0.01' },
    { key: 'triglycerides', type: 'number', step: '0.01' },
    {
      key: 'glucose_unit',
      type: 'select',
      optional: true,
      options: [
        { value: '', i18nKey: 'health-calculators-page.manual-unit-auto' },
        { value: 'mmol/l', label: 'mmol/L' },
        { value: 'mg/dl', label: 'mg/dL' },
      ],
    },
    {
      key: 'triglyceride_unit',
      type: 'select',
      optional: true,
      options: [
        { value: '', i18nKey: 'health-calculators-page.manual-unit-auto' },
        { value: 'mmol/l', label: 'mmol/L' },
        { value: 'mg/dl', label: 'mg/dL' },
      ],
    },
  ],
  EGFR: [
    {
      key: 'creatinine_unit',
      type: 'select',
      options: [
        { value: 'umol/L', label: 'µmol/L' },
        { value: 'mg/dL', label: 'mg/dL' },
      ],
    },
    {
      key: 'creatinine_value',
      type: 'number',
      step: '0.01',
      unitKey: 'creatinine_unit',
    },
    { key: 'age', type: 'number', step: '1', integer: true },
    {
      key: 'sex',
      type: 'select',
      options: [
        { value: 'M', i18nKey: 'health-calculators-page.manual-sex-male' },
        { value: 'F', i18nKey: 'health-calculators-page.manual-sex-female' },
      ],
    },
  ],
  FIB4: [
    { key: 'age', type: 'number', step: '1', integer: true },
    { key: 'ast', type: 'number', step: '0.1' },
    { key: 'alt', type: 'number', step: '0.1' },
    { key: 'platelets', type: 'number', step: '1', integer: true },
  ],
  SCORE2: [
    {
      key: 'non_hdl_unit',
      type: 'select',
      options: [
        { value: 'mmol/L', label: 'mmol/L' },
        { value: 'mg/dL', label: 'mg/dL' },
      ],
    },
    {
      key: 'non_hdl_cholesterol',
      type: 'number',
      step: '0.01',
      unitKey: 'non_hdl_unit',
    },
    {
      key: 'systolic_bp',
      type: 'number',
      step: '1',
      integer: true,
    },
    {
      key: 'diastolic_bp',
      type: 'number',
      step: '1',
      integer: true,
    },
    { key: 'age', type: 'number', step: '1', integer: true },
    {
      key: 'sex',
      type: 'select',
      options: [
        { value: 'M', i18nKey: 'health-calculators-page.manual-sex-male' },
        { value: 'F', i18nKey: 'health-calculators-page.manual-sex-female' },
      ],
    },
    {
      key: 'smoking',
      type: 'select',
      options: [
        { value: 'never', i18nKey: 'health-calculators-page.lifestyle-smoking-never' },
        { value: 'former', i18nKey: 'health-calculators-page.lifestyle-smoking-former' },
        { value: 'current', i18nKey: 'health-calculators-page.lifestyle-smoking-current' },
      ],
    },
  ],
  SCORE2_OP: [
    {
      key: 'non_hdl_unit',
      type: 'select',
      options: [
        { value: 'mmol/L', label: 'mmol/L' },
        { value: 'mg/dL', label: 'mg/dL' },
      ],
    },
    {
      key: 'non_hdl_cholesterol',
      type: 'number',
      step: '0.01',
      unitKey: 'non_hdl_unit',
    },
    {
      key: 'systolic_bp',
      type: 'number',
      step: '1',
      integer: true,
    },
    {
      key: 'diastolic_bp',
      type: 'number',
      step: '1',
      integer: true,
    },
    { key: 'age', type: 'number', step: '1', integer: true },
    {
      key: 'sex',
      type: 'select',
      options: [
        { value: 'M', i18nKey: 'health-calculators-page.manual-sex-male' },
        { value: 'F', i18nKey: 'health-calculators-page.manual-sex-female' },
      ],
    },
    {
      key: 'smoking',
      type: 'select',
      options: [
        { value: 'never', i18nKey: 'health-calculators-page.lifestyle-smoking-never' },
        { value: 'former', i18nKey: 'health-calculators-page.lifestyle-smoking-former' },
        { value: 'current', i18nKey: 'health-calculators-page.lifestyle-smoking-current' },
      ],
    },
  ],
  TOTAL_HDL_RATIO: [
    { key: 'total_chol', type: 'number', step: '0.01' },
    { key: 'hdl_chol', type: 'number', step: '0.01' },
  ],
  /** Backend: domyślnie mmol/L; opcjonalnie tc_unit / hdl_unit: mg/dl | mmol/l */
  NON_HDL: [
    {
      key: 'tc_unit',
      type: 'select',
      optional: true,
      options: [
        { value: '', i18nKey: 'health-calculators-page.manual-unit-default-mmol' },
        { value: 'mmol/l', label: 'mmol/L' },
        { value: 'mg/dl', label: 'mg/dL' },
      ],
    },
    { key: 'total_chol', type: 'number', step: '0.01', unitKey: 'tc_unit' },
    {
      key: 'hdl_unit',
      type: 'select',
      optional: true,
      options: [
        { value: '', i18nKey: 'health-calculators-page.manual-unit-default-mmol' },
        { value: 'mmol/l', label: 'mmol/L' },
        { value: 'mg/dl', label: 'mg/dL' },
      ],
    },
    { key: 'hdl_chol', type: 'number', step: '0.01', unitKey: 'hdl_unit' },
  ],
  TG_HDL_RATIO: [
    { key: 'triglycerides', type: 'number', step: '0.01' },
    { key: 'hdl_chol', type: 'number', step: '0.01' },
  ],
  AG_RATIO: [
    { key: 'albumin', type: 'number', step: '0.1' },
    { key: 'total_protein', type: 'number', step: '0.1' },
  ],
  NLR: [
    { key: 'neutrophils', type: 'number', step: '0.01' },
    { key: 'lymphocytes', type: 'number', step: '0.01' },
  ],
  // WHtR — no “Custom data” form; waist/height come from the health interview only (update_medical_data).
};

// Blood pressure is no longer in the health interview — it lives in blood-pressure-readings.
// SCORE2/SCORE2_OP: BP comes from logged readings or the “Custom data (preview)” form.
const MEDICAL_DATA_FIELDS = {
  EGFR: ['age', 'sex'],
  FIB4: ['age'],
  SCORE2: ['age', 'sex', 'smoking'],
  SCORE2_OP: ['age', 'sex', 'smoking'],
  WHTR: ['height_cm', 'waist_cm'],
};

// Calculators with persisted history (per LAB document or interview), e.g. eGFR
const HISTORY_CALCULATOR_TYPES = [
  'TYG',
  'EGFR',
  'FIB4',
  'SCORE2',
  'SCORE2_OP',
  'TOTAL_HDL_RATIO',
  'NON_HDL',
  'TG_HDL_RATIO',
  'AG_RATIO',
  'NLR',
  'WHTR',
  'PHQ9',
  'GAD7',
];

// Calculators without separate history (none currently)
const ON_DEMAND_CALCULATOR_TYPES = [];

// Result is shown in “Latest results” — hide “Preview from my data” for these.
const CALCULATORS_RESULT_IN_PANEL = [
  'SCORE2',
  'SCORE2_OP',
  'TOTAL_HDL_RATIO',
  'NON_HDL',
  'TG_HDL_RATIO',
  'AG_RATIO',
  'NLR',
  'WHTR',
];

/** `smoking_status` z available/details (SCORE2) albo legacy `smoking` (bool). */
function readSmokingStatusFromScore2Details(detailsForCalc) {
  if (!detailsForCalc || typeof detailsForCalc !== 'object') return null;
  const inputs = detailsForCalc.inputs && typeof detailsForCalc.inputs === 'object' ? detailsForCalc.inputs : {};
  const candidates = [
    inputs.smoking_status,
    inputs.lifestyle?.smoking_status,
    detailsForCalc.smoking_status,
    detailsForCalc.lifestyle?.smoking_status,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const v = candidates[i];
    if (typeof v === 'string' && ['never', 'former', 'current'].includes(v)) return v;
  }
  if (typeof inputs.smoking === 'boolean') return inputs.smoking ? 'current' : 'never';
  if (inputs.smoking === true || inputs.smoking === 'true') return 'current';
  if (inputs.smoking === false || inputs.smoking === 'false') return 'never';
  return null;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Stare wiersze NON_HDL: result_value często w mmol/L; obecnie API zwraca mg/dL. Heurystyka: wartości <35 jak mmol/L (typowy non-HDL w mmol/L <8; w mg/dL zwykle >50). */
const NON_HDL_MMOL_TO_MGDL = 38.669;

function nonHdlRawToMgDl(raw) {
  const num = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(num)) return num;
  return num > 0 && num < 35 ? num * NON_HDL_MMOL_TO_MGDL : num;
}

function normRiskCategoryKey(category) {
  return String(category ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');
}

function formatRiskCategoryLabel(category, lang = 'pl') {
  if (category == null || category === '') return '—';
  const key = String(lang || '').toLowerCase().startsWith('pl') ? 'pl' : 'en';
  const dict = RISK_CATEGORY_LABELS[key];
  const raw = String(category);
  if (dict[raw]) return dict[raw];
  const lower = raw.toLowerCase();
  if (dict[lower]) return dict[lower];
  const norm = normRiskCategoryKey(raw);
  if (dict[norm]) return dict[norm];
  if (raw.includes('_')) {
    return raw
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
  return raw;
}

function getCategoryBadge(category, lang = 'pl') {
  const raw = category == null ? '' : String(category);
  const norm = normRiskCategoryKey(raw);
  const color =
    CATEGORY_COLORS[raw] ||
    CATEGORY_COLORS[raw.toLowerCase()] ||
    CATEGORY_COLORS[norm] ||
    'secondary';
  const label = formatRiskCategoryLabel(category, lang);
  return <Badge bg={color}>{label}</Badge>;
}

// SCORE2 / SCORE2-OP: value as percentage (1–30% / 1–50%)
function formatCalculatorValue(calculatorType, value) {
  if (value == null) return value;
  if (calculatorType === 'SCORE2' || calculatorType === 'SCORE2_OP') {
    const num = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(num) ? `${num}%` : value;
  }
  if (calculatorType === 'TYG') {
    const num = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(num) ? num.toFixed(2) : value;
  }
  if (calculatorType === 'NON_HDL') {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(num)) return value;
    const mgDl = nonHdlRawToMgDl(num);
    return `${mgDl.toFixed(1)} mg/dL`;
  }
  return value;
}

function formatDate(dateStr, lang) {
  if (!dateStr) return null;
  return moment(dateStr).format(lang === 'pl' ? 'DD.MM.YYYY HH:mm' : 'MMM D, YYYY HH:mm');
}

/** Tylko data (bez godziny) — np. kolumna „Data badania” w tabeli historii w modalu. */
function formatTestDateOnly(dateStr, lang) {
  if (!dateStr) return null;
  const pl = lang === 'pl' || String(lang || '').startsWith('pl');
  return moment(dateStr).format(pl ? 'DD.MM.YYYY' : 'MMM D, YYYY');
}

function normalizeHistory(data) {
  return Array.isArray(data) ? data : data?.results ?? [];
}

function pickNumericField(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) {
    const v = obj[k];
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
}

/** BP used for SCORE2 — field names vary; there is not always a row in /blood-pressure-readings/. */
function extractBpFromScorePayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const sys = pickNumericField(payload, [
    'systolic_bp',
    'systolic',
    'systolic_mmhg',
    'bp_systolic',
  ]);
  const dia = pickNumericField(payload, [
    'diastolic_bp',
    'diastolic',
    'diastolic_mmhg',
    'bp_diastolic',
  ]);
  if (sys != null && dia != null) return { systolic: Math.round(sys), diastolic: Math.round(dia) };
  return null;
}

function getBpFallbackFromAvailableDetails(details) {
  if (!details || typeof details !== 'object') return null;
  for (const type of ['SCORE2', 'SCORE2_OP']) {
    const block = details[type];
    if (!block || typeof block !== 'object') continue;
    const bp = extractBpFromScorePayload(block.inputs) || extractBpFromScorePayload(block);
    if (bp) return { ...bp, source: 'available' };
  }
  return null;
}

function getBpFallbackFromDashboardEntry(dashboard) {
  if (!dashboard || typeof dashboard !== 'object') return null;
  for (const type of ['SCORE2', 'SCORE2_OP']) {
    const entry = dashboard[type];
    if (!entry || typeof entry !== 'object') continue;
    const bp = extractBpFromScorePayload(entry.inputs) || extractBpFromScorePayload(entry);
    if (bp) {
      return {
        ...bp,
        source: 'dashboard',
        calculated_at: entry.calculated_at || null,
      };
    }
  }
  return null;
}

function getBpFallbackFromScoreHistory(allHistory) {
  const rows = (allHistory || [])
    .filter((r) => r.calculator_type === 'SCORE2' || r.calculator_type === 'SCORE2_OP')
    .sort((a, b) => new Date(b.calculated_at || 0) - new Date(a.calculated_at || 0));
  for (const r of rows) {
    const payloads = [
      r.inputs,
      r.calculation_inputs,
      r.input_data,
      r.calculation_payload,
      r.metadata,
    ];
    for (const p of payloads) {
      const bp = extractBpFromScorePayload(p);
      if (bp) {
        return {
          ...bp,
          source: 'history',
          calculated_at: r.calculated_at,
          uuid: r.uuid,
        };
      }
    }
  }
  return null;
}

// ─── HistoryChart ─────────────────────────────────────────────────────────────

function HistoryChart({ calculatorType, history: historyProp }) {
  const { i18n, t } = useTranslation();
  const skipFetch = !!historyProp?.length;
  const { data: historyData, isLoading } = useFetchCalculatorHistoryQuery(
    { calculator_type: calculatorType },
    { skip: skipFetch },
  );
  const history = skipFetch ? historyProp : normalizeHistory(historyData);

  if (!historyProp?.length && isLoading) return <Spinner animation='border' size='sm' />;
  if (!history || history.length === 0)
    return <p className='text-muted small mb-0'>{t('health-calculators-page.history-empty')}</p>;

  // All charts: X axis left = oldest date, right = newest
  const sorted = [...history].sort(
    (a, b) => new Date(a.calculated_at || 0).getTime() - new Date(b.calculated_at || 0).getTime(),
  );
  const categories = sorted.map((r) => moment(r.calculated_at).format('DD.MM.YY'));
  const isScore2 = calculatorType === 'SCORE2' || calculatorType === 'SCORE2_OP';
  const isNonHdl = calculatorType === 'NON_HDL';
  const series = sorted.map((r) => {
    const raw = parseFloat((r.result_value || 0).toFixed(3));
    return isNonHdl ? nonHdlRawToMgDl(raw) : raw;
  });

  const options = {
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
    title: {
      text: t('health-calculators-page.history-chart-title'),
      align: 'left',
      style: { fontSize: '15px', fontWeight: 600, color: '#373d3f' },
    },
    stroke: { curve: 'smooth', width: 2 },
    xaxis: { categories },
    yaxis: {
      labels: {
        formatter: (v) => {
          if (isScore2) return `${Number(v).toFixed(1)}%`;
          if (isNonHdl) return `${Number(v).toFixed(1)} mg/dL`;
          return Number(v).toFixed(2);
        },
      },
    },
    markers: { size: 4 },
    grid: { borderColor: '#f1f1f1' },
    tooltip: {
      x: { show: true },
      y: {
        formatter: (v) => {
          if (isScore2) return `${v}%`;
          if (isNonHdl) return `${Number(v).toFixed(1)} mg/dL`;
          return v;
        },
      },
    },
  };

  return (
    <ReactApexChart
      key={`${calculatorType}-${i18n.language}`}
      options={options}
      series={[{ name: getCalculatorCardTitle(calculatorType, i18n.language), data: series }]}
      type='line'
      height={180}
    />
  );
}

// ─── Blood pressure (latest reading + systolic/diastolic chart) ───────────────

function BloodPressureDashboardTile({ lang, availableDetails, allHistory = [], dashboardData }) {
  const {
    data: rawBpData,
    isLoading,
    isUninitialized,
  } = useFetchBloodPressureReadingsQuery(FETCH_BLOOD_PRESSURE_READINGS_ARG);
  const [showChart, setShowChart] = useState(false);
  const [showAddReading, setShowAddReading] = useState(false);

  // RTK usually returns an array (transformResponse), but stale cache or regressions may yield { results } —
  // then Array.isArray(data) === false and only the SCORE2 fallback used to run.
  const readingsFromApi = React.useMemo(
    () => normalizeBloodPressureReadingsList(rawBpData),
    [rawBpData],
  );

  const score2Fallback = React.useMemo(() => {
    return (
      getBpFallbackFromAvailableDetails(availableDetails) ||
      getBpFallbackFromDashboardEntry(dashboardData) ||
      getBpFallbackFromScoreHistory(allHistory)
    );
  }, [availableDetails, dashboardData, allHistory]);

  const displayReadings = React.useMemo(() => {
    if (readingsFromApi.length > 0) return readingsFromApi;
    if (!score2Fallback) return [];
    // Do not show SCORE2 synthetic row until the first BP list fetch has finished
    if (isUninitialized || isLoading) return [];
    const dateStr = score2Fallback.calculated_at
      ? moment(score2Fallback.calculated_at).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');
    return [
      {
        id: score2Fallback.uuid || 'score2-inputs-fallback',
        date: dateStr,
        created_at: score2Fallback.calculated_at || null,
        systolic: score2Fallback.systolic,
        diastolic: score2Fallback.diastolic,
        _fromScore2Inputs: true,
      },
    ];
  }, [readingsFromApi, score2Fallback, isUninitialized, isLoading]);

  // One sorted list: oldest → newest; chart and tile (last item) share the same order.
  const timelineReadings = React.useMemo(
    () => sortBloodPressureReadingsOldestToNewest(displayReadings),
    [displayReadings],
  );

  const latest = React.useMemo(() => {
    if (!timelineReadings.length) return null;
    return timelineReadings[timelineReadings.length - 1];
  }, [timelineReadings]);

  const hasData = !!latest;
  const isSyntheticOnly = hasData && readingsFromApi.length === 0 && !!score2Fallback;

  const handleCardClick = () => {
    if (isLoading) return;
    if (hasData) setShowChart(true);
    else setShowAddReading(true);
  };

  return (
    <>
      <Card
        border='0'
        className='shadow-sm h-100'
        style={{ cursor: isLoading ? 'default' : 'pointer' }}
        onClick={handleCardClick}
      >
        <Card.Body>
          <div className='d-flex justify-content-between align-items-start mb-2'>
            <h6 className='mb-0 fw-bold'>{lang === 'pl' ? 'Ciśnienie krwi' : 'Blood pressure'}</h6>
            {hasData && <Badge bg='secondary'>mmHg</Badge>}
          </div>
          {isLoading ? (
            <Spinner animation='border' size='sm' />
          ) : hasData ? (
            <>
              <p className='display-6 fw-bold mb-1'>
                {latest.systolic}/{latest.diastolic}
              </p>
              <p className='text-muted small mb-2'>
                {isSyntheticOnly
                  ? lang === 'pl'
                    ? 'Wartość z kalkulacji SCORE2'
                    : 'Value from SCORE2 calculation'
                  : lang === 'pl'
                    ? 'Ostatni pomiar'
                    : 'Last reading'}
              </p>
              <p className='text-muted mb-0' style={{ fontSize: '0.75rem' }}>
                {moment(latest.date || latest.created_at).format(
                  lang === 'pl' ? 'DD.MM.YYYY' : 'MMM D, YYYY',
                )}
              </p>
              {isSyntheticOnly && (
                <p className='text-muted mt-1 mb-0' style={{ fontSize: '0.7rem' }}>
                  {lang === 'pl'
                    ? 'Aby mieć wykres z wielu dni, dodaj pomiary w sekcji poniżej.'
                    : 'Add readings below for a multi-day chart.'}
                </p>
              )}
              <p className='text-muted mt-2 mb-0' style={{ fontSize: '0.7rem' }}>
                {lang === 'pl' ? 'Kliknij, aby zobaczyć wykres' : 'Tap to view chart'}
              </p>
            </>
          ) : (
            <>
              <p className='text-muted small mb-2'>
                {lang === 'pl'
                  ? 'Brak pomiaru. Możesz dodać go w sekcji „Pomiary ciśnienia krwi” poniżej lub klikając ten kafelek.'
                  : 'No readings yet. Add one in the blood pressure section below, or tap this tile.'}
              </p>
              <p className='text-muted mb-0' style={{ fontSize: '0.7rem' }}>
                {lang === 'pl' ? 'Kliknij, aby dodać pomiar' : 'Tap to add a reading'}
              </p>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showChart} onHide={() => setShowChart(false)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {lang === 'pl' ? 'Ciśnienie krwi – wykres' : 'Blood pressure chart'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isSyntheticOnly && (
            <Alert variant='info' className='small py-2'>
              {lang === 'pl'
                ? 'Wykres opiera się na ciśnieniu użytym przy ostatniej kalkulacji SCORE2. Pełna historia wymaga zapisanych pomiarów w dzienniku.'
                : 'Chart uses blood pressure from your last SCORE2 calculation. Log readings below for a full history.'}
            </Alert>
          )}
          <BloodPressureReadingsChart readingsChronological={timelineReadings} lang={lang} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowChart(false)}>
            {lang === 'pl' ? 'Zamknij' : 'Close'}
          </Button>
        </Modal.Footer>
      </Modal>

      <AddBloodPressureModal
        show={showAddReading}
        onHide={() => setShowAddReading(false)}
        onSuccess={() => setShowAddReading(false)}
      />
    </>
  );
}

// ─── DashboardSummary ─────────────────────────────────────────────────────────

function DashboardSummary({
  onSelectCalculator,
  allHistory = [],
  isHistoryLoading,
  availableDetails,
}) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;

  // On-demand calculators — /dashboard_summary/ (sole source for their values)
  const { data: dashboard, isLoading: dashLoading } = useFetchCalculatorDashboardQuery({
    language: lang,
  });

  // Latest row from history per calculator (data passed from parent).
  // If missing in history, use dashboard_summary (backend may expose SCORE2 etc. live).
  const latestFromHistory = HISTORY_CALCULATOR_TYPES.reduce((acc, type) => {
    const latest = allHistory.find((r) => r.calculator_type === type);
    if (latest) {
      acc[type] = {
        value: latest.result_value,
        category: latest.risk_category,
        interpretation: lang === 'pl' ? latest.interpretation_pl : latest.interpretation_en,
        calculated_at: latest.calculated_at,
      };
      return acc;
    }
    const fromDashboard = dashboard?.[type];
    if (fromDashboard && (fromDashboard.value != null || fromDashboard.result_value != null)) {
      acc[type] = {
        value: fromDashboard.value ?? fromDashboard.result_value,
        category: fromDashboard.category || fromDashboard.risk_category,
        interpretation: fromDashboard.interpretation,
        calculated_at: fromDashboard.calculated_at || null,
      };
    }
    return acc;
  }, {});

  // Wyniki on-demand z dashboard_summary (wszystkie klucze z API + lista ON_DEMAND)
  const latestOnDemand = {};
  const dashboardKeys = Object.keys(dashboard || {});
  for (const type of ON_DEMAND_CALCULATOR_TYPES) {
    const entry = dashboard?.[type];
    if (entry) {
      latestOnDemand[type] = {
        value: entry.value ?? entry.result_value,
        category: entry.category || entry.risk_category,
        interpretation: entry.interpretation,
        calculated_at: null,
      };
    }
  }
  // Plus: any other dashboard key not present in history (e.g. new calculator from backend)
  for (const key of dashboardKeys) {
    if (HISTORY_CALCULATOR_TYPES.includes(key)) continue;
    if (latestOnDemand[key]) continue;
    const entry = dashboard[key];
    if (entry && (entry.value != null || entry.result_value != null)) {
      latestOnDemand[key] = {
        value: entry.value ?? entry.result_value,
        category: entry.category || entry.risk_category,
        interpretation: entry.interpretation,
        calculated_at: null,
      };
    }
  }

  const combined = { ...latestFromHistory, ...latestOnDemand };

  const isLoading = dashLoading || isHistoryLoading;

  if (isLoading) {
    return (
      <div className='text-center py-4'>
        <Spinner animation='border' variant='primary' />
      </div>
    );
  }

  const hasCalculatorTiles = Object.keys(combined).length > 0;

  return (
    <>
      <Row className='g-3'>
        <Col xs={12} sm={6} xl={4}>
          <BloodPressureDashboardTile
            lang={lang}
            availableDetails={availableDetails}
            allHistory={allHistory}
            dashboardData={dashboard}
          />
        </Col>
        {Object.entries(combined).map(([key, result]) => (
          <Col key={key} xs={12} sm={6} xl={4}>
            <Card
              border='0'
              className='shadow-sm h-100'
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectCalculator(key)}
            >
              <Card.Body>
                <div className='d-flex justify-content-between align-items-start mb-2'>
                  <h6 className='mb-0 fw-bold'>{getCalculatorCardTitle(key, lang)}</h6>
                  {getCategoryBadge(result.category, lang)}
                </div>
                <p className='display-6 fw-bold mb-1'>{formatCalculatorValue(key, result.value)}</p>
                <p className='text-muted small mb-2'>
                  {key === 'WHTR'
                    ? formatWhtrInterpretationForDisplay(result.interpretation)
                    : result.interpretation}
                </p>
                {result.calculated_at ? (
                  <p className='text-muted' style={{ fontSize: '0.75rem' }}>
                    {formatTestDateOnly(result.calculated_at, lang)}
                  </p>
                ) : (
                  <p className='text-muted' style={{ fontSize: '0.75rem' }}>
                    {t('health-calculators-page.dashboard-summary-on-demand')}
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      {!hasCalculatorTiles && (
        <Alert variant='info' className='mt-3 mb-0'>
          {t('health-calculators-page.dashboard-summary-empty')}
        </Alert>
      )}
    </>
  );
}

// ─── MedicalDataModal ─────────────────────────────────────────────────────────

function MedicalDataModal({ show, onHide, calculatorType, detailsForCalc, onSuccess }) {
  const { i18n, t } = useTranslation();
  const [updateMedicalData, { isLoading }] = useUpdateMedicalDataMutation();
  const [fields, setFields] = useState({
    age: '',
    sex: 'M',
    height_cm: '',
    waist_cm: '',
    smoking: 'never',
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const requiredFields = MEDICAL_DATA_FIELDS[calculatorType] || [];

  // WHtR: prefill z available/details. SCORE2/OP: palenie → lifestyle.smoking_status w wywiadzie.
  useEffect(() => {
    if (!show) return;
    if (calculatorType === 'WHTR' && detailsForCalc?.inputs) {
      const inputs = detailsForCalc.inputs;
      setFields((prev) => ({
        ...prev,
        ...(inputs.height_cm != null && { height_cm: String(inputs.height_cm) }),
        ...(inputs.waist_cm != null && { waist_cm: String(inputs.waist_cm) }),
      }));
    }
    if (calculatorType === 'SCORE2' || calculatorType === 'SCORE2_OP') {
      const status = readSmokingStatusFromScore2Details(detailsForCalc);
      if (status) setFields((prev) => ({ ...prev, smoking: status }));
    }
  }, [show, calculatorType, detailsForCalc]);

  const handleChange = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const body = {};
    if (fields.age)
      body.demographics = { ...(body.demographics || {}), age: parseInt(fields.age, 10) };
    if (fields.sex) body.demographics = { ...(body.demographics || {}), sex: fields.sex };
    if (fields.height_cm || fields.waist_cm) {
      body.anthropometry = {};
      if (fields.height_cm) body.anthropometry.height_cm = parseFloat(fields.height_cm);
      if (fields.waist_cm) body.anthropometry.waist_circumference_cm = parseFloat(fields.waist_cm);
    }
    // BP is not in the interview anymore — persist via /blood-pressure-readings/ or SCORE2 manual form.
    if (requiredFields.includes('smoking')) {
      body.lifestyle = { smoking_status: fields.smoking };
    }

    try {
      await updateMedicalData(body).unwrap();
      setSuccess(true);
      onSuccess && onSuccess();
      setTimeout(() => onHide(), 1500);
    } catch {
      setError(t('health-calculators-page.medical-data-save-error'));
    }
  };

  const FIELD_DEFS = useMemo(
    () => ({
      age: { label: t('health-calculators-page.manualFields.age'), type: 'number' },
      sex: {
        label: t('health-calculators-page.manualFields.sex'),
        type: 'select',
        options: [
          { value: 'M', label: t('health-calculators-page.manual-sex-male') },
          { value: 'F', label: t('health-calculators-page.manual-sex-female') },
        ],
      },
      height_cm: { label: t('health-calculators-page.manualFields.height_cm'), type: 'number' },
      waist_cm: { label: t('health-calculators-page.manualFields.waist_cm'), type: 'number' },
      smoking: {
        label: t('health-calculators-page.manualFields.smoking'),
        type: 'select',
        options: [
          { value: 'never', label: t('health-calculators-page.lifestyle-smoking-never') },
          { value: 'former', label: t('health-calculators-page.lifestyle-smoking-former') },
          { value: 'current', label: t('health-calculators-page.lifestyle-smoking-current') },
        ],
      },
    }),
    [t],
  );

  const visibleFields = requiredFields.filter((f) => FIELD_DEFS[f]);

  const isScore2 = calculatorType === 'SCORE2' || calculatorType === 'SCORE2_OP';
  const isWhtr = calculatorType === 'WHTR';

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('health-calculators-page.medical-data-modal-title', {
            calculator: getCalculatorCardTitle(calculatorType, i18n.language),
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success && (
          <Alert variant='success'>{t('health-calculators-page.medical-data-save-success')}</Alert>
        )}
        {error && <Alert variant='danger'>{error}</Alert>}
        {isWhtr && (
          <Alert variant='info' className='small py-2 mb-3'>
            {detailsForCalc?.inputs &&
            (detailsForCalc.inputs.height_cm != null || detailsForCalc.inputs.waist_cm != null) ? (
              <>
                {detailsForCalc.inputs.height_cm != null && (
                  <>
                    {t('health-calculators-page.whtr-alert-height-set', {
                      cm: Number(detailsForCalc.inputs.height_cm),
                    })}
                  </>
                )}
                {detailsForCalc.inputs.waist_cm != null && (
                  <>
                    {t('health-calculators-page.whtr-alert-waist-set', {
                      cm: Number(detailsForCalc.inputs.waist_cm),
                    })}
                  </>
                )}
                {Array.isArray(detailsForCalc.missing) && detailsForCalc.missing.length > 0 ? (
                  <>
                    {t('health-calculators-page.whtr-alert-update-prefix')}
                    {detailsForCalc.missing.includes('waist_cm') &&
                      t('health-calculators-page.whtr-alert-field-waist')}
                    {detailsForCalc.missing.includes('waist_cm') &&
                      detailsForCalc.missing.includes('height_cm') &&
                      t('health-calculators-page.whtr-alert-join-and')}
                    {detailsForCalc.missing.includes('height_cm') &&
                      t('health-calculators-page.whtr-alert-field-height')}
                    {t('health-calculators-page.whtr-alert-suffix-below')}
                  </>
                ) : (
                  t('health-calculators-page.whtr-alert-after-save')
                )}
              </>
            ) : (
              t('health-calculators-page.whtr-alert-fill-full')
            )}
          </Alert>
        )}
        {isScore2 && (
          <Alert variant='info' className='small py-2 mb-3'>
            {t('health-calculators-page.medical-data-score2-bp-hint')}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          {visibleFields.map((key) => {
            const def = FIELD_DEFS[key];
            return (
              <Form.Group key={key} className='mb-3'>
                <Form.Label>{def.label}</Form.Label>
                {def.type === 'select' ? (
                  <Form.Select
                    value={fields[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                  >
                    {def.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type={def.type}
                    value={fields[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                    required
                  />
                )}
              </Form.Group>
            );
          })}
          <div className='d-flex justify-content-end gap-2'>
            <Button variant='secondary' onClick={onHide}>
              {t('forms.cancel')}
            </Button>
            <Button variant='logo-green' type='submit' disabled={isLoading}>
              {isLoading ? <Spinner animation='border' size='sm' /> : t('forms.submit')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

// ─── Blood pressure readings (for SCORE2) ─────────────────────────────────────────────

function BloodPressureSection({ onSuccess }) {
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);

  const handleSuccess = async () => {
    if (onSuccess) await onSuccess();
  };

  return (
    <>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <h6 className='mb-0 fw-bold'>
          {t('health-calculators-page.blood-pressure-section-title')}
        </h6>
        <Button size='sm' variant='logo-green' onClick={() => setShowAdd(true)}>
          {t('dashboard.blood-pressure-add-reading')}
        </Button>
      </div>
      <p className='text-muted small mb-0'>
        <Trans
          i18nKey='health-calculators-page.blood-pressure-section-body'
          values={{
            latestResults: t('health-calculators-page.latest-results'),
            bpName: t('dashboard.blood-pressure-card-title'),
          }}
          components={{
            latestPanel: <span className='fw-semibold' />,
            bpTile: <strong />,
          }}
        />
      </p>
      <AddBloodPressureModal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        onSuccess={handleSuccess}
      />
    </>
  );
}

// ─── ManualCalculateModal ──────────────────────────────────────────────────────

function ManualCalculateModal({ show, onHide, calculatorType, onSuccess }) {
  const { i18n, t } = useTranslation();
  const [runCalculator, { isLoading }] = useRunCalculatorMutation();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState(null);

  const fields = MANUAL_INPUTS[calculatorType] || [];

  const handleChange = (key, value) => setInputs((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const parsedInputs = {};
    fields.forEach(({ key, type, integer, options, unitKey, optional }) => {
      if (calculatorType === 'TYG') return;
      if (unitKey) return; // value field handled separately (EGFR: creatinine_value; SCORE2: non_hdl_cholesterol)
      if (calculatorType === 'EGFR' && (key === 'creatinine_unit' || key === 'creatinine_value'))
        return;
      if (
        (calculatorType === 'SCORE2' || calculatorType === 'SCORE2_OP') &&
        (key === 'non_hdl_unit' || key === 'non_hdl_cholesterol')
      )
        return;
      if (
        calculatorType === 'NON_HDL' &&
        (key === 'tc_unit' || key === 'hdl_unit' || key === 'total_chol' || key === 'hdl_chol')
      )
        return;
      let val = inputs[key];
      if (type === 'select' && (val === undefined || val === '')) {
        if (optional) return;
        val = options?.[0]?.value;
      }
      if (val === undefined || val === '') return;
      if (type === 'number') parsedInputs[key] = integer ? parseInt(val, 10) : parseFloat(val);
      else if (key === 'smoking') {
        const s = String(val);
        if (['never', 'former', 'current'].includes(s)) {
          parsedInputs.smoking_status = s;
          parsedInputs.smoking = s === 'current';
        } else {
          parsedInputs.smoking = s === 'true';
        }
      } else parsedInputs[key] = val;
    });

    // eGFR: creatinine in µmol/L or mg/dL
    if (calculatorType === 'EGFR') {
      const unit =
        inputs.creatinine_unit ??
        fields.find((f) => f.key === 'creatinine_unit')?.options?.[0]?.value ??
        'umol/L';
      const value = parseFloat(inputs.creatinine_value);
      if (Number.isFinite(value)) {
        if (unit === 'mg/dL') {
          parsedInputs.creatinine = value;
          parsedInputs.creatinine_unit = 'mg/dL';
        } else {
          parsedInputs.creatinine_umol = value;
        }
      }
    }

    // SCORE2 / SCORE2_OP: non-HDL in mmol/L (default) or mg/dL + non_hdl_unit (backend converts)
    if (calculatorType === 'SCORE2' || calculatorType === 'SCORE2_OP') {
      const unit =
        inputs.non_hdl_unit ??
        fields.find((f) => f.key === 'non_hdl_unit')?.options?.[0]?.value ??
        'mmol/L';
      const value = parseFloat(inputs.non_hdl_cholesterol);
      if (Number.isFinite(value)) {
        parsedInputs.non_hdl_cholesterol = value;
        if (unit === 'mg/dL') parsedInputs.non_hdl_unit = 'mg/dL';
      }
    }

    // NON_HDL: TC + HDL; domyślnie mmol/l, opcjonalnie tc_unit / hdl_unit: mg/dl | mmol/l
    if (calculatorType === 'NON_HDL') {
      const tc = parseFloat(inputs.total_chol);
      const hdl = parseFloat(inputs.hdl_chol);
      if (!Number.isFinite(tc) || !Number.isFinite(hdl)) {
        setError(t('health-calculators-page.manual-preview-error'));
        return;
      }
      parsedInputs.total_chol = tc;
      parsedInputs.hdl_chol = hdl;
      const tcU = String(inputs.tc_unit ?? '').trim().toLowerCase();
      const hdlU = String(inputs.hdl_unit ?? '').trim().toLowerCase();
      parsedInputs.tc_unit = tcU === 'mg/dl' ? 'mg/dl' : 'mmol/l';
      parsedInputs.hdl_unit = hdlU === 'mg/dl' ? 'mg/dl' : 'mmol/l';
    }

    if (calculatorType === 'TYG') {
      const fg = parseFloat(inputs.fasting_glucose);
      const tg = parseFloat(inputs.triglycerides);
      if (!Number.isFinite(fg) || !Number.isFinite(tg)) {
        setError(t('health-calculators-page.manual-preview-error'));
        return;
      }
      parsedInputs.fasting_glucose = fg;
      parsedInputs.triglycerides = tg;
      const gu = inputs.glucose_unit;
      if (gu != null && String(gu).trim() !== '') parsedInputs.glucose_unit = gu;
      const tu = inputs.triglyceride_unit;
      if (tu != null && String(tu).trim() !== '') parsedInputs.triglyceride_unit = tu;
    }

    try {
      const result = await runCalculator({
        calculator_type: calculatorType,
        inputs: parsedInputs,
      }).unwrap();
      onSuccess(result);
      onHide();
    } catch (err) {
      setError(
        err?.data?.detail || err?.data?.error || t('health-calculators-page.manual-preview-error'),
      );
    }
  };

  const manualFieldLabel = (key) => t(`health-calculators-page.manualFields.${key}`);
  const selectOptionLabel = (o) => (o.i18nKey ? t(o.i18nKey) : o.label);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('health-calculators-page.manual-preview-title-prefix')}{' '}
          {getCalculatorCardTitle(calculatorType, i18n.language)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='warning' className='small py-2'>
          {t('health-calculators-page.manual-preview-warning')}
        </Alert>
        {error && <Alert variant='danger'>{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          {fields.map((field) => (
            <Form.Group key={field.key} className='mb-3'>
              <Form.Label>{manualFieldLabel(field.key)}</Form.Label>
              {field.type === 'select' ? (
                <Form.Select
                  value={
                    inputs[field.key] !== undefined && inputs[field.key] !== null
                      ? inputs[field.key]
                      : field.optional
                        ? ''
                        : field.options[0]?.value || ''
                  }
                  onChange={(e) => handleChange(field.key, e.target.value)}
                >
                  {field.options.map((o, idx) => (
                    <option key={`${field.key}-${idx}`} value={o.value}>
                      {selectOptionLabel(o)}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type={field.type}
                  step={field.step}
                  value={inputs[field.key] ?? ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  required
                />
              )}
            </Form.Group>
          ))}
          <div className='d-flex justify-content-end gap-2'>
            <Button variant='secondary' onClick={onHide}>
              {t('forms.cancel')}
            </Button>
            <Button variant='logo-green' type='submit' disabled={isLoading}>
              {isLoading ? (
                <Spinner animation='border' size='sm' />
              ) : (
                t('health-calculators-page.manual-preview-calculate')
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

// ─── ResultModal ──────────────────────────────────────────────────────────────

function ResultModal({ show, onHide, result, calculatorType, lang }) {
  const { t } = useTranslation();
  if (!result) return null;
  const value = result.value ?? result.result_value;
  const category = result.category || result.risk_category;
  const interpretation =
    lang === 'pl'
      ? result.interpretation_pl || result.interpretation
      : result.interpretation_en || result.interpretation;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('health-calculators-page.result-preview-title-prefix')}{' '}
          {getCalculatorCardTitle(calculatorType, lang)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-center'>
        <Alert variant='warning' className='text-start small py-2 mb-3'>
          {calculatorType === 'SCORE2' || calculatorType === 'SCORE2_OP' ? (
            <Trans
              i18nKey='health-calculators-page.result-preview-alert-score2'
              values={{
                latestResults: t('health-calculators-page.latest-results'),
                bloodPressure: t('health-calculators-page.blood-pressure-section-title'),
              }}
              components={{
                latest: <span className='fw-semibold' />,
                bp: <span className='fw-semibold' />,
              }}
            />
          ) : (
            t('health-calculators-page.result-preview-alert-general')
          )}
        </Alert>
        <p className='display-4 fw-bold mb-2'>{formatCalculatorValue(calculatorType, value)}</p>
        <div className='mb-3'>{getCategoryBadge(category, lang)}</div>
        {interpretation && (
          <p className='text-muted'>
            {calculatorType === 'WHTR'
              ? formatWhtrInterpretationForDisplay(interpretation)
              : interpretation}
          </p>
        )}
        {result.calculated_at && (
          <p className='text-muted small'>{formatDate(result.calculated_at, lang)}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          {t('forms.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── QuestionnaireModal ───────────────────────────────────────────────────────

function translateQuestionnaireSeverity(level, t) {
  if (level == null || level === '') return '';
  const slug = String(level).toLowerCase();
  const k = `health-calculators-page.questionnaire-severity-${slug}`;
  const out = t(k);
  return out === k ? String(level) : out;
}

function QuestionnaireModal({ show, onHide, type, onSuccess }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const { data: questionsData, isLoading: questionsLoading } = useFetchQuestionnaireQuestionsQuery(
    { type, language: lang },
    { skip: !show },
  );
  const [submitQuestionnaire, { isLoading: submitting }] = useSubmitQuestionnaireMutation();
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const questions = questionsData?.questions || [];

  const handleAnswer = (idx, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const allAnswered =
    questions.length > 0 && answers.filter((a) => a !== undefined).length === questions.length;

  const handleSubmit = async () => {
    setError(null);
    try {
      const res = await submitQuestionnaire({ questionnaire_type: type, answers }).unwrap();
      setResult(res);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.data?.detail || t('health-calculators-page.questionnaire-submit-error'));
    }
  };

  const handleClose = () => {
    setAnswers([]);
    setResult(null);
    setError(null);
    onHide();
  };

  const SEVERITY_COLORS = {
    minimal: 'success',
    mild: 'info',
    moderate: 'warning',
    moderately_severe: 'danger',
    severe: 'danger',
  };

  return (
    <Modal show={show} onHide={handleClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          {type === 'PHQ9'
            ? t('health-calculators-page.questionnaire-title-phq9')
            : t('health-calculators-page.questionnaire-title-gad7')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}
        {result ? (
          <div className='text-center py-3'>
            <p className='h5 mb-3'>{t('health-calculators-page.questionnaire-result-heading')}</p>
            <p className='display-4 fw-bold mb-2'>
              {t('health-calculators-page.questionnaire-score-points', {
                score: result.total_score,
              })}
            </p>
            <Badge bg={SEVERITY_COLORS[result.severity_level] || 'secondary'} className='fs-6 mb-3'>
              {translateQuestionnaireSeverity(result.severity_level, t)}
            </Badge>
            <p className='text-muted'>
              {t('health-calculators-page.questionnaire-completed-prefix')}{' '}
              {formatDate(result.completed_at, lang)}
            </p>
          </div>
        ) : questionsLoading ? (
          <div className='text-center py-4'>
            <Spinner animation='border' variant='primary' />
          </div>
        ) : (
          <>
            <p className='text-muted mb-4'>
              <Trans
                i18nKey='health-calculators-page.questionnaire-intro'
                components={{ strong: <strong /> }}
              />
            </p>
            {questions.map((q, idx) => (
              <div key={q.id} className='mb-4'>
                <p className='fw-bold mb-2'>
                  {idx + 1}. {lang === 'pl' ? q.text_pl : q.text_en}
                </p>
                <div className='d-flex flex-wrap gap-2'>
                  {[0, 1, 2, 3].map((val) => (
                    <Button
                      key={val}
                      size='sm'
                      variant={answers[idx] === val ? 'logo-green' : 'outline-secondary'}
                      onClick={() => handleAnswer(idx, val)}
                    >
                      {val} – {t(`health-calculators-page.questionnaire-scale-${val}`)}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      {!result && (
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose}>
            {t('forms.cancel')}
          </Button>
          <Button variant='logo-green' onClick={handleSubmit} disabled={!allAnswered || submitting}>
            {submitting ? (
              <Spinner animation='border' size='sm' />
            ) : (
              t('health-calculators-page.questionnaire-submit')
            )}
          </Button>
        </Modal.Footer>
      )}
      {result && (
        <Modal.Footer>
          <Button variant='logo-green' onClick={handleClose}>
            {t('forms.close')}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

// ─── LearnMoreModal ────────────────────────────────────────────────────────────

function LearnMoreModal({ show, onHide, calcType, descriptions }) {
  const { i18n, t } = useTranslation();
  const desc = descriptions?.[calcType];
  const name = desc?.name || getCalculatorCardTitle(calcType, i18n.language) || calcType;
  const shortDescription = desc?.short_description;
  const learnMore = desc?.learn_more;

  return (
    <Modal show={show} onHide={onHide} size='lg' centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {shortDescription && <p className='text-muted mb-3'>{shortDescription}</p>}
        {learnMore ? (
          <div className='learn-more-content' style={{ whiteSpace: 'pre-wrap' }}>
            {learnMore}
          </div>
        ) : (
          <p className='text-muted mb-0'>{t('health-calculators-page.learn-more-no-extra')}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          {t('forms.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── CalculatorCard ───────────────────────────────────────────────────────────

function CalculatorCard({
  calcType,
  available,
  availableDetails,
  hasHistory,
  onOpenHistory,
  onSuccess,
  onOpenLearnMore,
}) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const [runCalculator, { isLoading }] = useRunCalculatorMutation();
  const [showMedicalDataModal, setShowMedicalDataModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [calcError, setCalcError] = useState(null);

  const isQuestionnaire = calcType === 'PHQ9' || calcType === 'GAD7';
  const hasManualInputs = !isQuestionnaire && !!MANUAL_INPUTS[calcType];
  const desc = CALCULATOR_DESCRIPTIONS[calcType] || {};
  const whtrDetails = calcType === 'WHTR' ? availableDetails?.WHTR : null;

  // After adding a BP reading (SCORE2) `available` becomes true — close “Fill in data” modal.
  useEffect(() => {
    if ((calcType === 'SCORE2' || calcType === 'SCORE2_OP') && available && showMedicalDataModal) {
      setShowMedicalDataModal(false);
    }
  }, [calcType, available, showMedicalDataModal]);

  // Preview from user profile — no inputs; backend auto-fills
  const handlePreviewFromData = async () => {
    setCalcError(null);
    try {
      const result = await runCalculator({ calculator_type: calcType }).unwrap();
      setLastResult(result);
      setShowResult(true);
      onSuccess && onSuccess();
    } catch (err) {
      const msg = err?.data?.detail || err?.data?.error || '';
      if (msg.toLowerCase().includes('insufficient')) {
        setCalcError('insufficient');
      } else {
        setCalcError(msg || t('health-calculators-page.calc-error-generic'));
      }
    }
  };

  const handleManualSuccess = (result) => {
    setLastResult(result);
    setShowResult(true);
  };

  return (
    <>
      <Card border='0' className='shadow-sm h-100'>
        <Card.Body className='d-flex flex-column'>
          <div className='d-flex justify-content-between align-items-start mb-2'>
            <h6 className='fw-bold mb-0'>{getCalculatorCardTitle(calcType, lang)}</h6>
            <Badge bg={available || hasHistory ? 'success' : 'secondary'} pill>
              {available || hasHistory
                ? t('health-calculators-page.calc-badge-ready')
                : t('health-calculators-page.calc-badge-no-data')}
            </Badge>
          </div>
          <p className='text-muted small flex-grow-1 mb-2'>{lang === 'pl' ? desc.pl : desc.en}</p>
          {onOpenLearnMore && (
            <Button
              variant='outline-info'
              size='sm'
              className='mb-2'
              onClick={() => onOpenLearnMore(calcType)}
            >
              <InformationCircleIcon className='icon icon-xs me-1' />
              {t('health-calculators-page.calc-btn-about')}
            </Button>
          )}

          {/* History source note — non-questionnaire calculators only */}
          {!isQuestionnaire && (
            <p className='text-muted mb-3' style={{ fontSize: '0.7rem' }}>
              {t('health-calculators-page.calc-history-from-documents')}
            </p>
          )}

          {calcError === 'insufficient' && (
            <Alert variant='warning' className='py-2 px-3 small'>
              {t('health-calculators-page.calc-insufficient-data')}
            </Alert>
          )}
          {calcError && calcError !== 'insufficient' && (
            <Alert variant='danger' className='py-2 px-3 small'>
              {calcError}
            </Alert>
          )}
          {calcType === 'WHTR' && !available && whtrDetails?.inputs && (
            <Alert variant='info' className='py-2 px-3 small'>
              {whtrDetails.inputs.height_cm != null && (
                <>
                  {t('health-calculators-page.whtr-alert-height-set', {
                    cm: Number(whtrDetails.inputs.height_cm),
                  })}
                </>
              )}
              {whtrDetails.inputs.waist_cm != null && (
                <>
                  {t('health-calculators-page.whtr-alert-waist-set', {
                    cm: Number(whtrDetails.inputs.waist_cm),
                  })}
                </>
              )}
              {Array.isArray(whtrDetails.missing) && whtrDetails.missing.length > 0 && (
                <>
                  {t('health-calculators-page.whtr-card-update-prefix')}
                  {whtrDetails.missing.includes('waist_cm') &&
                    t('health-calculators-page.whtr-card-field-waist')}
                  {whtrDetails.missing.includes('waist_cm') &&
                    whtrDetails.missing.includes('height_cm') &&
                    t('health-calculators-page.whtr-card-join-and')}
                  {whtrDetails.missing.includes('height_cm') &&
                    t('health-calculators-page.whtr-card-field-height')}
                  {t('health-calculators-page.whtr-card-suffix')}
                </>
              )}
            </Alert>
          )}

          <div className='d-flex flex-wrap gap-2 mt-auto'>
            {isQuestionnaire ? (
              // PHQ-9 / GAD-7: questionnaire persists the result to history
              <>
                <Button size='sm' variant='logo-green' onClick={() => setShowQuestionnaire(true)}>
                  {t('health-calculators-page.calc-btn-fill-questionnaire')}
                </Button>
                {hasHistory && (
                  <Button
                    size='sm'
                    variant='outline-secondary'
                    onClick={() => onOpenHistory(calcType)}
                  >
                    {t('health-calculators-page.calc-btn-history')}
                  </Button>
                )}
              </>
            ) : (
              <>
                {hasHistory && (
                  <Button size='sm' variant='logo-green' onClick={() => onOpenHistory(calcType)}>
                    {t('health-calculators-page.calc-btn-view-history')}
                  </Button>
                )}
                {available && !CALCULATORS_RESULT_IN_PANEL.includes(calcType) && (
                  <Button
                    size='sm'
                    variant='outline-primary'
                    onClick={handlePreviewFromData}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner animation='border' size='sm' />
                    ) : (
                      t('health-calculators-page.calc-btn-preview-my-data')
                    )}
                  </Button>
                )}
                {hasManualInputs && (
                  <Button
                    size='sm'
                    variant='outline-secondary'
                    onClick={() => setShowManualModal(true)}
                  >
                    {t('health-calculators-page.calc-btn-custom-data-preview')}
                  </Button>
                )}
              </>
            )}
          </div>
        </Card.Body>
      </Card>

      <MedicalDataModal
        show={showMedicalDataModal}
        onHide={() => setShowMedicalDataModal(false)}
        calculatorType={calcType}
        detailsForCalc={availableDetails?.[calcType]}
        onSuccess={onSuccess}
      />
      {hasManualInputs && (
        <ManualCalculateModal
          show={showManualModal}
          onHide={() => setShowManualModal(false)}
          calculatorType={calcType}
          onSuccess={handleManualSuccess}
        />
      )}
      <ResultModal
        show={showResult}
        onHide={() => setShowResult(false)}
        result={lastResult}
        calculatorType={calcType}
        lang={lang}
      />
      {isQuestionnaire && (
        <QuestionnaireModal
          show={showQuestionnaire}
          onHide={() => setShowQuestionnaire(false)}
          type={calcType}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
}

// ─── EditResultModal ──────────────────────────────────────────────────────────

function EditResultModal({ show, onHide, result, calculatorType }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const [patchResult, { isLoading }] = usePatchCalculatorResultMutation();

  const [fields, setFields] = useState({
    result_value: '',
    risk_category: '',
    interpretation_pl: '',
    interpretation_en: '',
  });
  const [error, setError] = useState(null);

  // Prefill fields from the selected row when the modal opens
  React.useEffect(() => {
    if (result) {
      setFields({
        result_value: result.result_value ?? '',
        risk_category: result.risk_category ?? '',
        interpretation_pl: result.interpretation_pl ?? '',
        interpretation_en: result.interpretation_en ?? '',
      });
      setError(null);
    }
  }, [result]);

  const handleChange = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await patchResult({
        uuid: result.uuid,
        result_value: parseFloat(fields.result_value),
        risk_category: fields.risk_category || undefined,
        interpretation_pl: fields.interpretation_pl || undefined,
        interpretation_en: fields.interpretation_en || undefined,
      }).unwrap();
      onHide();
    } catch (err) {
      const detail = err?.data?.detail || '';
      if (err?.status === 403) {
        setError(t('health-calculators-page.edit-result-403'));
      } else {
        setError(detail || t('health-calculators-page.edit-result-update-failed'));
      }
    }
  };

  if (!result) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('health-calculators-page.edit-result-title', {
            calculator: getCalculatorCardTitle(calculatorType, lang),
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Form.Group className='mb-3'>
            <Form.Label>{t('health-calculators-page.edit-result-value-label')}</Form.Label>
            <Form.Control
              type='number'
              step='0.001'
              value={fields.result_value}
              onChange={(e) => handleChange('result_value', e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>{t('health-calculators-page.edit-result-risk-category')}</Form.Label>
            <Form.Control
              type='text'
              value={fields.risk_category}
              onChange={(e) => handleChange('risk_category', e.target.value)}
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>{t('health-calculators-page.edit-result-interpretation-pl')}</Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={fields.interpretation_pl}
              onChange={(e) => handleChange('interpretation_pl', e.target.value)}
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label>{t('health-calculators-page.edit-result-interpretation-en')}</Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={fields.interpretation_en}
              onChange={(e) => handleChange('interpretation_en', e.target.value)}
            />
          </Form.Group>
          <div className='d-flex justify-content-end gap-2'>
            <Button variant='secondary' onClick={onHide}>
              {t('forms.cancel')}
            </Button>
            <Button variant='logo-green' type='submit' disabled={isLoading}>
              {isLoading ? (
                <Spinner animation='border' size='sm' />
              ) : (
                t('health-calculators-page.edit-result-save-changes')
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

// ─── HistorySection ───────────────────────────────────────────────────────────

function HistorySection({ calculatorType, preloadedHistory }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const skipFetch = !!preloadedHistory?.length;
  const { data: historyData, isLoading } = useFetchCalculatorHistoryQuery(
    { calculator_type: calculatorType },
    { skip: skipFetch },
  );
  const [deleteResult, { isLoading: deleting }] = useDeleteCalculatorResultMutation();

  const [editTarget, setEditTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const history = skipFetch ? preloadedHistory : normalizeHistory(historyData);

  const handleDelete = async (r) => {
    if (!window.confirm(t('health-calculators-page.delete-confirm'))) return;
    setDeleteError(null);
    try {
      await deleteResult({ uuid: r.uuid }).unwrap();
    } catch (err) {
      if (err?.status === 403) {
        setDeleteError(t('health-calculators-page.delete-result-403'));
      } else {
        setDeleteError(err?.data?.detail || t('health-calculators-page.delete-result-failed'));
      }
    }
  };

  if (!skipFetch && isLoading) return <Spinner animation='border' size='sm' />;
  if (!history || history.length === 0) {
    return <p className='text-muted'>{t('health-calculators-page.history-empty-calculator')}</p>;
  }

  return (
    <>
      {deleteError && (
        <Alert variant='danger' dismissible onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <div className='mb-4'>
        <HistoryChart calculatorType={calculatorType} history={history} />
      </div>
      <Table responsive striped bordered hover size='sm'>
        <thead>
          <tr>
            <th>{t('health-calculators-page.table-test-date')}</th>
            <th>{t('health-calculators-page.table-result')}</th>
            <th>{t('health-calculators-page.table-category')}</th>
            <th>{t('health-calculators-page.table-interpretation')}</th>
            <th>{t('health-calculators-page.table-source')}</th>
          </tr>
        </thead>
        <tbody>
          {history.map((r) => (
            <tr key={r.uuid}>
              <td>{formatTestDateOnly(r.calculated_at, lang)}</td>
              <td className='fw-bold'>{formatCalculatorValue(calculatorType, r.result_value)}</td>
              <td>{getCategoryBadge(r.risk_category, lang)}</td>
              <td className='small'>
                {calculatorType === 'WHTR'
                  ? formatWhtrInterpretationForDisplay(
                      lang === 'pl' ? r.interpretation_pl : r.interpretation_en,
                    )
                  : lang === 'pl'
                    ? r.interpretation_pl
                    : r.interpretation_en}
              </td>
              <td>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  {r.is_manual ? (
                    <Badge bg='info' pill>
                      {t('health-calculators-page.source-manual')}
                    </Badge>
                  ) : calculatorType === 'PHQ9' ||
                    calculatorType === 'GAD7' ||
                    calculatorType === 'WHTR' ? (
                    <Badge bg='secondary' pill className='text-white'>
                      {t('health-calculators-page.source-questionnaire')}
                    </Badge>
                  ) : r.related_document != null ? (
                    <Link
                      to={`/medical-documents/${r.related_document}`}
                      className='text-decoration-none'
                    >
                      <Badge bg='secondary' pill className='text-white'>
                        {t('health-calculators-page.source-document')}
                      </Badge>
                    </Link>
                  ) : (
                    <Badge bg='secondary' pill>
                      {t('health-calculators-page.source-profile-recalc')}
                    </Badge>
                  )}
                  {r.is_manual && (
                    <div className='d-flex flex-wrap gap-1'>
                      <Button size='sm' variant='outline-primary' onClick={() => setEditTarget(r)}>
                        {t('health-calculators-page.action-edit')}
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-danger'
                        onClick={() => handleDelete(r)}
                        disabled={deleting}
                      >
                        {t('health-calculators-page.action-delete')}
                      </Button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <EditResultModal
        show={!!editTarget}
        onHide={() => setEditTarget(null)}
        result={editTarget}
        calculatorType={calculatorType}
      />
    </>
  );
}

// ─── CalculatorsPage ──────────────────────────────────────────────────────────

export default function CalculatorsPage() {
  const { i18n, t } = useTranslation();
  const pageLang = i18n.language;
  const {
    data: available,
    isLoading: availLoading,
    refetch: refetchAvailable,
  } = useFetchAvailableCalculatorsQuery();
  const { data: dashboardSummary = {}, refetch: refetchDashboard } =
    useFetchCalculatorDashboardQuery({ language: i18n.language });
  const { data: descriptions = {} } = useFetchCalculatorDescriptionsQuery({
    language: i18n.language,
  });

  // Fetch full history (no type filter) — backend returns all rows; limit=100 for coverage
  const {
    data: allHistoryData,
    isLoading: isHistoryLoading,
    refetch: refetchHistory,
  } = useFetchCalculatorHistoryQuery({
    ordering: '-calculated_at',
    limit: 100,
  });
  const { refetch: refetchMentalHealth } = useFetchMentalHealthHistoryQuery();

  const allHistoryRaw = normalizeHistory(allHistoryData);
  // Single source of truth: health-calculators API. PHQ9/GAD7 only from there — after backend deletes, UI matches.
  const allHistory = allHistoryRaw
    .filter((r) => r.calculator_type !== 'HOMA_IR')
    .filter((r) => HISTORY_CALCULATOR_TYPES.includes(r.calculator_type))
    .sort((a, b) => new Date(b.calculated_at || 0) - new Date(a.calculated_at || 0));

  // Calculator types that have at least one history row
  const typesWithHistory = new Set(allHistory.map((r) => r.calculator_type));

  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showOnDemandInfo, setShowOnDemandInfo] = useState(false);
  const [onDemandInfoKey, setOnDemandInfoKey] = useState(null);
  const [learnMoreCalcType, setLearnMoreCalcType] = useState(null);

  const allCalculators = Object.keys(CALCULATOR_LABELS);

  const handleOpenHistory = (calcType) => {
    if (ON_DEMAND_CALCULATOR_TYPES.includes(calcType)) {
      setOnDemandInfoKey(calcType);
      setShowOnDemandInfo(true);
    } else {
      setSelectedHistory(calcType);
      setShowHistoryModal(true);
    }
  };

  const handleOpenLearnMore = (calcType) => setLearnMoreCalcType(calcType);

  const handleCalcSuccess = async () => {
    await Promise.all([
      refetchAvailable(),
      refetchHistory(),
      refetchDashboard(),
      refetchMentalHealth(),
    ]);
  };

  // On-demand calculators: "ready" = available from API OR a value in dashboard_summary (e.g. TC/HDL)
  const isAvailableForCard = (calcType) => {
    const fromApi = available?.[calcType] ?? false;
    if (ON_DEMAND_CALCULATOR_TYPES.includes(calcType)) {
      const hasDashboardResult =
        dashboardSummary[calcType] != null &&
        (dashboardSummary[calcType].value != null ||
          dashboardSummary[calcType].result_value != null);
      return fromApi || !!hasDashboardResult;
    }
    return fromApi;
  };

  return (
    <div className='p-4 bg-light min-vh-100'>
      <Row className='mb-4'>
        <Col>
          <h1 className='h3 fw-bold mb-1'>{t('health-calculators-page.title')}</h1>
          <p className='text-muted mb-0'>{t('health-calculators-page.subtitle')}</p>
        </Col>
      </Row>

      <Row className='mb-4'>
        <Col>
          <Card border='0' className='shadow'>
            <Card.Body>
              <h2 className='h5 fw-bold mb-3'>{t('health-calculators-page.latest-results')}</h2>
              <DashboardSummary
                onSelectCalculator={handleOpenHistory}
                allHistory={allHistory}
                isHistoryLoading={isHistoryLoading}
                availableDetails={available?.details}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className='mb-4 g-3'>
        <Col xs={12} md={6} lg={5}>
          <Card border='0' className='shadow-sm h-100'>
            <Card.Body>
              <BloodPressureSection onSuccess={handleCalcSuccess} />
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} lg={5}>
          <Card border='0' className='shadow-sm h-100'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <h6 className='mb-0 fw-bold'>
                  {t('health-calculators-page.health-interview-section-title')}
                </h6>
                <Button as={Link} to={Routes.HealthDataSurvey.path} variant='logo-green' size='sm'>
                  {t('health-calculators-page.health-interview-section-cta')}
                </Button>
              </div>
              <p className='text-muted small mb-0'>
                {t('health-calculators-page.health-interview-section-body')}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className='mb-2'>
        <Col>
          <h2 className='h5 fw-bold mb-3'>
            {t('health-calculators-page.available-calculators-heading')}
          </h2>
        </Col>
      </Row>

      {availLoading ? (
        <div className='text-center py-4'>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : (
        <Row className='g-3 mb-4'>
          {allCalculators.map((calcType) => (
            <Col key={calcType} xs={12} sm={6} xl={4} xxl={3}>
              <CalculatorCard
                calcType={calcType}
                available={isAvailableForCard(calcType)}
                availableDetails={available?.details}
                hasHistory={typesWithHistory.has(calcType)}
                onOpenHistory={handleOpenHistory}
                onOpenLearnMore={handleOpenLearnMore}
                onSuccess={handleCalcSuccess}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* History modal — calculators with persisted history */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size='xl' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('health-calculators-page.history-modal-title-prefix')}{' '}
            {getCalculatorCardTitle(selectedHistory, pageLang)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedHistory && (
            <HistorySection
              calculatorType={selectedHistory}
              preloadedHistory={allHistory.filter((r) => r.calculator_type === selectedHistory)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowHistoryModal(false)}>
            {t('forms.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Info modal — on-demand calculators without history */}
      <Modal show={showOnDemandInfo} onHide={() => setShowOnDemandInfo(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{getCalculatorCardTitle(onDemandInfoKey, pageLang)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant='info' className='mb-0'>
            <p className='fw-bold mb-1'>
              {t('health-calculators-page.on-demand-no-history-title')}
            </p>
            <p className='mb-0 small'>
              <Trans
                i18nKey='health-calculators-page.on-demand-no-history-body'
                values={{ name: getCalculatorCardTitle(onDemandInfoKey, pageLang) }}
                components={[<strong key='on-demand-name' />]}
              />
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowOnDemandInfo(false)}>
            {t('forms.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* “Learn more” modal — calculator copy from API */}
      <LearnMoreModal
        show={!!learnMoreCalcType}
        onHide={() => setLearnMoreCalcType(null)}
        calcType={learnMoreCalcType}
        descriptions={descriptions}
      />
    </div>
  );
}
